import React, { useCallback, useMemo, useState } from 'react';
import {
    Badge,
    Box,
    Button,
    Card,
    Divider,
    Group,
    SimpleGrid,
    Slider,
    Stack,
    Tabs,
    Text,
    ThemeIcon,
    Title,
    Tooltip,
} from '@mantine/core';
import {
    IconBuildingBroadcastTower,
    IconChartLine,
    IconInfoCircle,
    IconReportMoney,
    IconRotateClockwise,
    IconTrendingUp,
} from '@tabler/icons-react';
import {
    CASH_FLOW_HORIZON_MONTHS,
    DEFAULT_MEDIA_CALCULATOR_INPUTS,
    MEDIA_SOURCE_DATA,
    NATIONAL_MNF_CPM,
    computeMediaCalculator,
    type MediaCalculatorInputs,
} from '../../pricing/mediaCalculator';
import CashFlowChart from './CashFlowChart';

const currency = (n: number, maximumFractionDigits = 0) =>
    n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits });

const number = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 0 });

type SliderControlProps = {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min: number;
    max: number;
    step: number;
    /** How to render the live value beside the label. */
    format: (value: number) => string;
    description?: string;
};

const SliderControl: React.FC<SliderControlProps> = ({
    label,
    value,
    onChange,
    min,
    max,
    step,
    format,
    description,
}) => (
    <Box>
        <Group justify="space-between" align="baseline" mb={4} wrap="nowrap">
            <Text size="sm" fw={500}>
                {label}
            </Text>
            <Text size="sm" fw={700} c="indigo">
                {format(value)}
            </Text>
        </Group>
        <Slider
            value={value}
            onChange={onChange}
            min={min}
            max={max}
            step={step}
            label={format}
            color="indigo"
            size="sm"
        />
        {description && (
            <Text size="xs" c="dimmed" mt={4}>
                {description}
            </Text>
        )}
    </Box>
);

type StatProps = {
    label: string;
    value: string;
    hint?: string;
    accent?: 'indigo' | 'teal' | 'red' | 'gray';
    emphasize?: boolean;
};

const Stat: React.FC<StatProps> = ({ label, value, hint, accent = 'gray', emphasize }) => (
    <Card withBorder padding="md" radius="md" shadow={emphasize ? 'sm' : 'xs'}>
        <Group gap={4} mb={4} wrap="nowrap">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600} lh={1.2}>
                {label}
            </Text>
            {hint && (
                <Tooltip label={hint} multiline w={240} withArrow position="top">
                    <ThemeIcon size={14} radius="xl" variant="subtle" color="gray">
                        <IconInfoCircle size={12} />
                    </ThemeIcon>
                </Tooltip>
            )}
        </Group>
        <Text size={emphasize ? '1.6rem' : 'xl'} fw={700} c={accent === 'gray' ? undefined : accent}>
            {value}
        </Text>
    </Card>
);

const MediaCalculator: React.FC = () => {
    const [inputs, setInputs] = useState<MediaCalculatorInputs>(DEFAULT_MEDIA_CALCULATOR_INPUTS);

    const patch = useCallback(
        <K extends keyof MediaCalculatorInputs>(key: K, value: MediaCalculatorInputs[K]) => {
            setInputs((prev) => ({ ...prev, [key]: value }));
        },
        []
    );

    const reset = useCallback(() => setInputs(DEFAULT_MEDIA_CALCULATOR_INPUTS), []);

    const out = useMemo(() => computeMediaCalculator(inputs), [inputs]);

    const breakEvenDisplay =
        out.breakEvenMonths == null
            ? '—'
            : out.breakEvenMonths <= CASH_FLOW_HORIZON_MONTHS
              ? `${out.breakEvenMonths} mo`
              : `${out.breakEvenMonths} mo (beyond 5 yr)`;

    return (
        <Stack gap="lg" maw={1040}>
            <Group justify="space-between" align="flex-start" wrap="wrap">
                <Group gap="sm">
                    <ThemeIcon size="lg" radius="md" variant="gradient" gradient={{ from: 'indigo', to: 'cyan' }}>
                        <IconChartLine size={22} stroke={1.5} />
                    </ThemeIcon>
                    <div>
                        <Title order={2}>Media Pricing Calculator</Title>
                        <Text c="dimmed" size="sm">
                            In-venue media value vs. national TV, and Watchly's deployment unit economics
                        </Text>
                    </div>
                </Group>
                <Button
                    variant="subtle"
                    color="gray"
                    size="sm"
                    leftSection={<IconRotateClockwise size={16} />}
                    onClick={reset}
                >
                    Reset to defaults
                </Button>
            </Group>

            <Tabs defaultValue="advertiser" keepMounted={false}>
                <Tabs.List>
                    <Tabs.Tab value="advertiser" leftSection={<IconBuildingBroadcastTower size={16} />}>
                        Advertiser view
                    </Tabs.Tab>
                    <Tabs.Tab value="operator" leftSection={<IconReportMoney size={16} />}>
                        Operator economics
                    </Tabs.Tab>
                </Tabs.List>

                {/* ---------------- Advertiser view ---------------- */}
                <Tabs.Panel value="advertiser" pt="lg">
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                        <Card withBorder padding="lg" radius="md" shadow="xs">
                            <Text fw={600} mb="md">
                                Audience & spot pricing
                            </Text>
                            <Stack gap="lg">
                                <SliderControl
                                    label="BWW locations reached"
                                    value={inputs.locationsDeployed}
                                    onChange={(v) => patch('locationsDeployed', v)}
                                    min={50}
                                    max={MEDIA_SOURCE_DATA.bwwTotalLocations}
                                    step={1}
                                    format={number}
                                    description={`× ${MEDIA_SOURCE_DATA.viewersPerLocationMnf} viewers per location on MNF night = ${number(
                                        out.bwwAudience
                                    )} in-venue viewers`}
                                />
                                <SliderControl
                                    label="Spot price Watchly charges"
                                    value={inputs.spotPrice}
                                    onChange={(v) => patch('spotPrice', v)}
                                    min={1_000}
                                    max={20_000}
                                    step={1}
                                    format={(v) => currency(v)}
                                    description={`CPM-derived suggestion: ${currency(
                                        out.suggestedSpotPrice
                                    )} (national CPM ${currency(NATIONAL_MNF_CPM, 2)} × ${
                                        MEDIA_SOURCE_DATA.inVenuePremiumMultiple
                                    }× in-venue premium)`}
                                />
                            </Stack>

                            <Divider my="lg" />

                            <Stack gap={6}>
                                <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                                    Source data (fixed)
                                </Text>
                                <Group justify="space-between">
                                    <Text size="sm" c="dimmed">
                                        National MNF spot (Ad Age 24–25)
                                    </Text>
                                    <Text size="sm">{currency(MEDIA_SOURCE_DATA.nationalMnfAdCostPerSpot)}</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm" c="dimmed">
                                        National MNF viewers (Nielsen 25)
                                    </Text>
                                    <Text size="sm">{number(MEDIA_SOURCE_DATA.nationalMnfViewers)}</Text>
                                </Group>
                                <Group justify="space-between">
                                    <Text size="sm" c="dimmed">
                                        National CPM
                                    </Text>
                                    <Text size="sm">{currency(NATIONAL_MNF_CPM, 2)}</Text>
                                </Group>
                            </Stack>
                        </Card>

                        <Stack gap="lg">
                            <SimpleGrid cols={2} spacing="md">
                                <Stat
                                    label="Implied TV cost to reach this audience"
                                    value={currency(out.impliedTvCostPerSpot)}
                                    accent="gray"
                                    hint="What it would cost on national TV to reach the same in-venue audience, pro-rated from the national MNF spot price."
                                />
                                <Stat
                                    label="Watchly spot price"
                                    value={currency(inputs.spotPrice)}
                                    accent="indigo"
                                    hint="Price Watchly charges the advertiser for one network spot."
                                />
                            </SimpleGrid>
                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <Group gap="sm" mb={4}>
                                    <ThemeIcon size="md" radius="md" variant="light" color="teal">
                                        <IconTrendingUp size={18} />
                                    </ThemeIcon>
                                    <Text fw={600}>Premium captured over implied TV cost</Text>
                                </Group>
                                <Text size="2rem" fw={700} c="teal">
                                    {currency(out.premiumOverImpliedTv)}
                                </Text>
                                <Badge mt="xs" variant="light" color="teal" size="lg">
                                    {out.premiumMultiple.toFixed(1)}× the implied TV cost
                                </Badge>
                                <Text size="sm" c="dimmed" mt="md">
                                    The in-venue audience is captive, sound-on, and high-intent. At{' '}
                                    {currency(inputs.spotPrice)} per spot the advertiser pays an effective CPM of{' '}
                                    {currency(out.watchlyEffectiveCpm, 2)} versus {currency(NATIONAL_MNF_CPM, 2)}{' '}
                                    nationally — for an audience that is physically in a sports-betting context.
                                </Text>
                            </Card>
                        </Stack>
                    </SimpleGrid>
                </Tabs.Panel>

                {/* ---------------- Operator economics ---------------- */}
                <Tabs.Panel value="operator" pt="lg">
                    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
                        <Card withBorder padding="lg" radius="md" shadow="xs">
                            <Text fw={600} mb="md">
                                Deployment inputs
                            </Text>
                            <Stack gap="lg">
                                <SliderControl
                                    label="BWW locations deployed"
                                    value={inputs.locationsDeployed}
                                    onChange={(v) => patch('locationsDeployed', v)}
                                    min={1}
                                    max={MEDIA_SOURCE_DATA.bwwTotalLocations}
                                    step={1}
                                    format={number}
                                />
                                <SliderControl
                                    label="Screens per location"
                                    value={inputs.screensPerLocation}
                                    onChange={(v) => patch('screensPerLocation', v)}
                                    min={1}
                                    max={20}
                                    step={1}
                                    format={number}
                                />
                                <SliderControl
                                    label="Hardware cost per screen"
                                    value={inputs.hardwareCostPerScreen}
                                    onChange={(v) => patch('hardwareCostPerScreen', v)}
                                    min={100}
                                    max={3_000}
                                    step={50}
                                    format={(v) => currency(v)}
                                />
                                <SliderControl
                                    label="Install cost per location"
                                    value={inputs.installCostPerLocation}
                                    onChange={(v) => patch('installCostPerLocation', v)}
                                    min={0}
                                    max={2_000}
                                    step={50}
                                    format={(v) => currency(v)}
                                />
                                <SliderControl
                                    label="SaaS fee per location / month"
                                    value={inputs.saasFeePerLocationPerMonth}
                                    onChange={(v) => patch('saasFeePerLocationPerMonth', v)}
                                    min={0}
                                    max={1_000}
                                    step={10}
                                    format={(v) => currency(v)}
                                />
                                <SliderControl
                                    label="Ad spots sold per MNF game"
                                    value={inputs.adSpotsSoldPerGame}
                                    onChange={(v) => patch('adSpotsSoldPerGame', v)}
                                    min={0}
                                    max={20}
                                    step={1}
                                    format={number}
                                />
                                <SliderControl
                                    label="Spot price Watchly charges"
                                    value={inputs.spotPrice}
                                    onChange={(v) => patch('spotPrice', v)}
                                    min={1_000}
                                    max={20_000}
                                    step={1}
                                    format={(v) => currency(v)}
                                />
                                <SliderControl
                                    label="Watchly commission on ad revenue"
                                    value={Math.round(inputs.commissionFraction * 100)}
                                    onChange={(v) => patch('commissionFraction', v / 100)}
                                    min={0}
                                    max={50}
                                    step={1}
                                    format={(v) => `${v}%`}
                                />
                                <SliderControl
                                    label="MNF games per season"
                                    value={inputs.gamesPerSeason}
                                    onChange={(v) => patch('gamesPerSeason', v)}
                                    min={1}
                                    max={25}
                                    step={1}
                                    format={number}
                                />
                            </Stack>
                        </Card>

                        <Stack gap="lg">
                            <SimpleGrid cols={2} spacing="md">
                                <Stat
                                    label="Upfront deployment cost"
                                    value={currency(out.upfrontDeploymentCost)}
                                    accent="red"
                                    hint="Locations × (screens × hardware/screen + install/location)."
                                />
                                <Stat
                                    label="Break-even"
                                    value={breakEvenDisplay}
                                    accent="teal"
                                    emphasize
                                    hint="Months until cumulative revenue covers the upfront deployment cost, using monthly-averaged revenue."
                                />
                                <Stat
                                    label="Annual SaaS revenue"
                                    value={currency(out.annualSaasRevenue)}
                                    accent="indigo"
                                    hint="Locations × SaaS fee/location/month × 12."
                                />
                                <Stat
                                    label="Annual media commission"
                                    value={currency(out.annualMediaCommissionRevenue)}
                                    accent="indigo"
                                    hint="Spots/game × spot price × games/season × commission."
                                />
                            </SimpleGrid>

                            <Card withBorder padding="lg" radius="md" shadow="sm">
                                <Group gap="sm" mb={4}>
                                    <ThemeIcon size="md" radius="md" variant="light" color="teal">
                                        <IconReportMoney size={18} />
                                    </ThemeIcon>
                                    <Text fw={600}>Total annual revenue</Text>
                                </Group>
                                <Text size="2rem" fw={700} c="teal">
                                    {currency(out.totalAnnualRevenue)}
                                </Text>
                                <Text size="sm" c="dimmed" mt={4}>
                                    {currency(out.monthlyRevenue)} / month blended across SaaS and media commission.
                                </Text>
                            </Card>

                            <Card withBorder padding="lg" radius="md" shadow="xs">
                                <Text fw={600} mb={2}>
                                    Cumulative cash flow
                                </Text>
                                <Text size="xs" c="dimmed" mb="sm">
                                    {CASH_FLOW_HORIZON_MONTHS} months from deployment · starts at the upfront cost and
                                    accrues blended monthly revenue
                                </Text>
                                <CashFlowChart data={out.cashFlow} breakEvenMonths={out.breakEvenMonths} />
                            </Card>
                        </Stack>
                    </SimpleGrid>
                </Tabs.Panel>
            </Tabs>
        </Stack>
    );
};

export default MediaCalculator;
